import React from 'react'
import { COLORS } from '@btime/constants'
import { getExtension, getImageLabel, getImageSrc, isDocumentImage } from '@btime/helpers'
import QRCode from 'qrcode.react'
import { useTranslation } from 'react-i18next'

import { useImagesSize } from './options/_form/PdfOptionsFormHandler'
import Grid, { Col } from './Grid'
import Image from './Image'
import LabelValue from './LabelValue'
import Separator from './Separator'
import Icon from '../shapes/Icon'
import Text from './Text'
import { isEmpty } from 'lodash'

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp']

const getExtensionFromSrc = src => {
  if (!src || typeof src !== 'string') return ''
  const path = src.split('?')[0]
  const ext = path.split('.').pop() || ''
  return ext.toLowerCase()
}

const isImageItem = item => {
  if (!item) return false
  if (isDocumentImage(item)) return true
  const src = getImageSrc(item)
  return src && IMAGE_EXTENSIONS.includes(getExtensionFromSrc(src))
}

const styles = {
  captionWrapper: { padding: 5, backgroundColor: COLORS.light1, borderBottomLeftRadius: 2, borderBottomRightRadius: 2 },
  downloadButton: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    backgroundColor: 'transparent',
    border: 'solid 1px #D3D7E8',
    borderRadius: '0.4rem',
    padding: '0.4rem 1.1rem',
    cursor: 'pointer',
    textDecoration: 'none'
  },
  avoidBreak: {
    pageBreakInside: 'avoid',
    breakInside: 'avoid'
  },
  imageCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  }
}

const GalleryImageItem = ({ images, width }) => {
  const { t } = useTranslation('components')
  const numberOfImages = images.length
  const imageNumberLabel = t('pdf.imagesCount', { count: numberOfImages })

  if (numberOfImages <= 0) return null

  return (
    <>
      <LabelValue label={t('images')} value={imageNumberLabel} />
      <Separator weight={2} />
      <Grid justify="flex-start" align="flex-start" wrap className="gallery-images">
        {images.map(image => {
          const label = getImageLabel(image)
          const src = getImageSrc(image)

          return (
            <Col key={image.id} width={width} gutter={5} style={styles.avoidBreak}>
              <div style={styles.imageCell}>
                <Image src={src} />
                {!!label && (
                  <div style={styles.captionWrapper}>
                    <Text size="xxsmall">{label}</Text>
                  </div>
                )}
              </div>
            </Col>
          )
        })}
      </Grid>
    </>
  )
}

const GalleryFileItem = ({ files, width }) => {
  const { t } = useTranslation('components')
  const filteredFiles = files.filter(file => !!file && !isEmpty(file))

  const numberOfFiles = filteredFiles.length
  const fileNumberLabel = t('pdf.filesCount', { count: numberOfFiles })

  if (numberOfFiles <= 0) return null

  return (
    <>
      <LabelValue label={t('files')} value={fileNumberLabel} />
      <Separator weight={2} />
      <Grid justify="flex-start" align="flex-start" wrap>
        {filteredFiles.map(file => {
          const src = getImageSrc(file)
          const extension = getExtension(file.src) || getExtensionFromSrc(src)

          return (
            <Col key={file.id} width={width} gutter={15} style={styles.avoidBreak}>
              <div style={styles.imageCell}>
                <Grid align="center" direction="column">
                  <QRCode value={src} renderAs="svg" fgColor="black" bgColor="white" height="100%" width="100%" />
                  <Separator weight={3} />
                  <a href={src} target="_blank" rel="noreferrer" style={styles.downloadButton}>
                    <Icon name="RiDownloadCloud2Line" size={'0.8rem'} color={COLORS.dark2} />
                    <Text size="xsmall" bold>
                      {t('pdf.download', { extension })}
                    </Text>
                  </a>
                </Grid>
              </div>
            </Col>
          )
        })}
      </Grid>
    </>
  )
}

const Gallery = ({ items }) => {
  const { imagesPerRow } = useImagesSize()
  const width = 100 / imagesPerRow + '%'

  const images = React.useMemo(() => (items || []).filter(isImageItem), [items])
  const files = React.useMemo(
    () => (items || []).filter(item => item && !isEmpty(item) && !isImageItem(item)),
    [items]
  )

  return (
    <Grid className="gallery" direction={'column'} align="stretch">
      <GalleryImageItem images={images} width={width} />
      <GalleryFileItem files={files} width={width} />
    </Grid>
  )
}

export default Gallery
